import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { api } from "../../../lib/api";
import { isSessionExpired } from "../../../lib/api";
import { toast } from "sonner";
import {
  NormalizedRow,
  offeredResolutionsFor,
  Resolution,
  SAFE_RAW_FIELD_LABELS,
  SafeRawField,
} from "../lib/rowContract";
import {
  NormalizedDuplicate,
  COMPARE_FIELDS,
  COMPARE_FIELD_LABELS,
  CompareField,
  suggestedForRow,
} from "../lib/duplicateContract";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  MinusCircle,
  RefreshCw,
} from "lucide-react";

const RESOLUTION_LABELS: Record<Resolution, string> = {
  skip: "Skip this row",
  create_anyway: "Create anyway",
  update_existing: "Update existing",
  manual_review: "Mark for manual review",
};

const RESOLUTION_DESCRIPTIONS: Record<Resolution, string> = {
  skip: "Do not import this row.",
  create_anyway: "Import as a new Contact even though a similar one exists.",
  update_existing:
    "Overwrite matching fields on the existing Contact with incoming values.",
  manual_review: "Flag this row for a human decision. This blocks commit.",
};

/**
 * "Compare Incoming Data" (never "Merge") dialog. Renders only the safe
 * server-provided summary fields (masked email/phone) and the
 * conflicting/non-conflicting field lists that already came back on the
 * duplicate document. No arbitrary CRM reads; no field-level merge UI.
 *
 * Resolution actions are strictly the union of `suggested_resolutions`
 * across the candidates the backend returned for this row.
 * `update_existing` requires a candidate pick from that same list; the
 * dialog refuses to submit any other id.
 */
export function DuplicateResolverDialog({
  row,
  duplicates,
  open,
  onClose,
  jobId,
  onResolved,
}: {
  row: NormalizedRow;
  duplicates: NormalizedDuplicate[];
  open: boolean;
  onClose: () => void;
  jobId: string;
  onResolved: () => void;
}) {
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(
    duplicates[0]?.existingContactId ?? null,
  );
  const [confirming, setConfirming] = useState<Resolution | null>(null);
  const [inFlight, setInFlight] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [candidateStale, setCandidateStale] = useState(false);
  const openerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedCandidateId(duplicates[0]?.existingContactId ?? null);
      setConfirming(null);
      setConflict(false);
      setCandidateStale(false);
    }
  }, [open, row.id, duplicates]);

  const offered = offeredResolutionsFor(row.status, suggestedForRow(duplicates));
  const selectedCandidate =
    duplicates.find((d) => d.existingContactId === selectedCandidateId) ?? null;

  const submit = async (resolution: Resolution) => {
    if (inFlight) return; // double-submit guard
    setInFlight(true);
    setConflict(false);
    setCandidateStale(false);
    try {
      const body: {
        resolution: Resolution;
        target_contact_id?: string;
        if_version: number;
      } = { resolution, if_version: row.version };
      if (resolution === "update_existing") {
        if (!selectedCandidate) {
          setCandidateStale(true);
          return;
        }
        // Refuse arbitrary ids — must be in the backend-provided list.
        if (!row.duplicateMatchIds.includes(selectedCandidate.existingContactId)) {
          setCandidateStale(true);
          return;
        }
        body.target_contact_id = selectedCandidate.existingContactId;
      }
      await api.post(`/import/jobs/${jobId}/rows/${row.id}/resolve`, body);
      toast.success("Resolution saved");
      onResolved();
      onClose();
    } catch (e: unknown) {
      if (isSessionExpired(e)) return; // suppressed via existing singleton
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        setConflict(true);
        toast.error("This row changed. Reload to continue.");
        onResolved(); // force server-truth refresh in the parent
      } else {
        const detail = (e as { response?: { data?: { detail?: unknown } } })
          ?.response?.data?.detail;
        const code =
          typeof detail === "string"
            ? detail
            : typeof (detail as { code?: string })?.code === "string"
            ? (detail as { code: string }).code
            : null;
        if (code === "target_not_in_candidates" || code === "resolution_not_offered") {
          setCandidateStale(true);
        } else {
          toast.error(code || "Could not save resolution.");
        }
      }
    } finally {
      setInFlight(false);
      setConfirming(null);
    }
  };

  const askConfirm = (r: Resolution) => setConfirming(r);
  const isDestructive = (r: Resolution) => r === "update_existing" || r === "skip";

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? null : onClose())}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="duplicate-resolver-dialog"
        aria-describedby={`duplicate-desc-${row.id}`}
      >
        <DialogHeader>
          <DialogTitle>Compare Incoming Data · Row #{row.rowIndex}</DialogTitle>
          <DialogDescription id={`duplicate-desc-${row.id}`}>
            {duplicates.length === 0
              ? "No duplicate candidates on this row."
              : `Comparing the incoming CSV row against ${duplicates.length} match${
                  duplicates.length === 1 ? "" : "es"
                } already in RemediKeep.`}
          </DialogDescription>
        </DialogHeader>

        {conflict && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
            data-testid="duplicate-conflict-banner"
          >
            <RefreshCw className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-medium">This row changed elsewhere</div>
              <div>Reload to see the latest state before resolving.</div>
            </div>
          </div>
        )}
        {candidateStale && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
            data-testid="duplicate-candidate-stale"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              This candidate is no longer valid. Close and reopen to refresh candidates.
            </div>
          </div>
        )}

        {duplicates.length > 1 && (
          <div className="space-y-1" data-testid="duplicate-candidate-picker">
            <label className="text-xs font-medium text-slate-600">
              Choose which existing Contact to compare / update:
            </label>
            <div className="flex flex-wrap gap-2">
              {duplicates.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setSelectedCandidateId(d.existingContactId)}
                  aria-pressed={d.existingContactId === selectedCandidateId}
                  className={`rounded-full border px-3 py-1 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 ${
                    d.existingContactId === selectedCandidateId
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
                  }`}
                  data-testid={`candidate-chip-${d.existingContactId}`}
                >
                  {d.matchStrength} · {d.matchScore}
                </button>
              ))}
            </div>
          </div>
        )}

        {selectedCandidate && (
          <CompareTable
            row={row}
            candidate={selectedCandidate}
          />
        )}

        <DialogFooter className="pt-2 flex-wrap gap-2">
          <div className="mr-auto text-xs text-slate-500 self-center">
            {offered.length === 0
              ? "No resolutions available for this row."
              : "Pick a resolution — server accepts only offered actions."}
          </div>
          {offered.map((r) => (
            <Button
              key={r}
              size="sm"
              variant={r === "update_existing" ? "default" : "outline"}
              className="rounded-full"
              disabled={inFlight}
              onClick={() =>
                isDestructive(r) ? askConfirm(r) : submit(r)
              }
              data-testid={`resolve-action-${r}`}
            >
              {RESOLUTION_LABELS[r]}
            </Button>
          ))}
        </DialogFooter>

        {confirming && (
          <div
            role="alertdialog"
            className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm"
            data-testid={`resolve-confirm-${confirming}`}
          >
            <div className="font-medium text-slate-900">
              Confirm: {RESOLUTION_LABELS[confirming]}
            </div>
            <div className="mt-0.5 text-slate-600">
              {RESOLUTION_DESCRIPTIONS[confirming]}
            </div>
            <div className="mt-2 flex gap-2 justify-end">
              <Button
                size="sm"
                variant="ghost"
                className="rounded-full"
                onClick={() => setConfirming(null)}
                disabled={inFlight}
                data-testid={`resolve-confirm-cancel`}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="rounded-full"
                onClick={() => submit(confirming)}
                disabled={inFlight}
                data-testid={`resolve-confirm-go`}
              >
                {inFlight ? "Applying…" : `Yes, ${RESOLUTION_LABELS[confirming].toLowerCase()}`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function fieldValue(
  summary: Partial<Record<string, string | null>>,
  key: string,
): string {
  const v = summary[key];
  if (!v) return "";
  return String(v);
}

function CompareTable({
  row,
  candidate,
}: {
  row: NormalizedRow;
  candidate: NormalizedDuplicate;
}) {
  const rowsForCompare: CompareField[] = COMPARE_FIELDS as unknown as CompareField[];
  const conflicting = new Set(candidate.conflictingFields);
  const matching = new Set(candidate.nonConflictingFields);

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[480px]">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
          <tr>
            <th className="text-left px-3 py-2 font-medium">Field</th>
            <th className="text-left px-3 py-2 font-medium">Incoming (CSV)</th>
            <th className="text-left px-3 py-2 font-medium">Existing Contact</th>
          </tr>
        </thead>
        <tbody data-testid="compare-table-body">
          {rowsForCompare.map((f) => {
            const inc = fieldValue(candidate.sourceSummary, f);
            const ex = fieldValue(candidate.existingSummary, f);
            const isConflict = conflicting.has(f);
            const isMatch = matching.has(f);
            return (
              <tr
                key={f}
                className={`border-t border-slate-200 ${
                  isConflict ? "bg-amber-50/60" : ""
                }`}
                data-testid={`compare-row-${f}`}
                data-conflict={isConflict ? "true" : "false"}
                data-matching={isMatch ? "true" : "false"}
              >
                <td className="px-3 py-2 text-slate-600 text-xs">
                  {COMPARE_FIELD_LABELS[f]}
                  {isConflict && (
                    <span className="ml-1 text-amber-700">•</span>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-xs">
                  {inc || <span className="text-slate-400">—</span>}
                </td>
                <td className="px-3 py-2 font-mono text-xs">
                  <span className="inline-flex items-center gap-1">
                    {isConflict ? (
                      <ArrowRight className="w-3 h-3 text-amber-700" />
                    ) : isMatch ? (
                      <CheckCircle2 className="w-3 h-3 text-emerald-700" />
                    ) : (
                      <MinusCircle className="w-3 h-3 text-slate-300" />
                    )}
                    {ex || <span className="text-slate-400">—</span>}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      <div className="border-t border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
        Existing values are shown with masked email/phone from RemediKeep — full
        Contact details never leave the server.
      </div>
    </div>
  );
}
